import { playersAbove } from "../document/band.ts";
import { EMPTY_TILE } from "../document/packed-tile.ts";
import { sameJson } from "../document/same-json.ts";
import { dropStep, entriesById, firstUses, pickOverflow, placementsFor, pullStep, removeStep, stepsCommand, wholeStep, type PickStepOf } from "../document/tileset-picks.ts";
import { canonicalPlace } from "../document/layout.ts";
import { COLLISION_CELLS_PER_TILE, type LayerDocument, type MapDocument, type MapPlace, type WorldDocument } from "../document/types.ts";
import { bandAfterMove } from "../editor/layer-move.ts";
import { MAP_SIDE_MOST } from "../editor/map-edits.ts";
import { seamRemovalsFor } from "./layout-ops.ts";
import type { EmptyLayer, EmptyMap, IntentOf } from "./op-types.ts";
import { MAP_GROUPS_MAX, MAP_LAYERS_MAX, WORLD_MAPS_MAX, gridBytes, mapOf, passesFieldLimits, passesGridLimit, type Resolution } from "./resolve-types.ts";

/**
 * Resolving the intents that change structure (ADR-086 decision 19): a layer added empty above a named layer,
 * removed by id or moved above a named layer with its side of the Players band; the band by its first layer
 * above; groups; a map added without content or removed; tilesets added by id and removed when unused. Each
 * places by a neighbour's id, never by an index another builder may have shifted, and reads the index from
 * the document it is given.
 */

/**
 * A layer without its content: what an intent carries of it.
 *
 * @param layer The layer.
 * @returns Its kind, id, name, group and terrain fields.
 */
export function withoutContent(layer: LayerDocument): EmptyLayer {
  const head = { id: layer.id, name: layer.name };
  const membership = layer.group === undefined ? {} : { group: layer.group };
  if (layer.kind !== "terrain") {
    return { kind: layer.kind, ...head, ...membership };
  }
  return { kind: layer.kind, ...head, ...(layer.autoName === undefined ? {} : { autoName: layer.autoName }), ...membership, terrain: layer.terrain, variant: layer.variant, ...(layer.faceHeight === undefined ? {} : { faceHeight: layer.faceHeight }) };
}

/**
 * A map without its layers' content: what an intent carries of it, its band named by the first layer above.
 *
 * @param map The map.
 * @returns Its fields, its groups and its layers without content.
 */
export function withoutLayerContent(map: MapDocument): EmptyMap {
  return {
    id: map.id,
    name: map.name,
    width: map.width,
    height: map.height,
    seed: map.seed,
    ...(map.place === undefined ? {} : { place: map.place }),
    ...(map.host === undefined ? {} : { host: { map: map.host.map, arrival: map.host.arrival } }),
    ...(map.share === undefined ? {} : { share: map.share }),
    ...(map.capacity === undefined ? {} : { capacity: map.capacity }),
    ...(map.atmosphere === undefined ? {} : { atmosphere: map.atmosphere }),
    ...(map.mapGroup === undefined ? {} : { mapGroup: map.mapGroup }),
    ...(map.draft === true ? { draft: true as const } : {}),
    first: map.layers[playersAbove(map)]?.id ?? null,
    groups: [...(map.groups ?? [])],
    layers: map.layers.map(withoutContent),
    properties: map.properties,
  };
}

/**
 * An empty layer of a map's size: a grid of nothing or an empty list.
 *
 * @param layer The layer without content.
 * @param map The map's size.
 * @returns The layer.
 */
function emptyLayer(layer: EmptyLayer, map: { readonly width: number; readonly height: number }): LayerDocument {
  const fine = map.width * map.height * COLLISION_CELLS_PER_TILE * COLLISION_CELLS_PER_TILE;
  switch (layer.kind) {
    case "terrain":
      return { ...layer, corners: new Uint8Array((map.width + 1) * (map.height + 1)) };
    case "tile":
      return { ...layer, kind: layer.kind, tiles: new Uint32Array(map.width * map.height).fill(EMPTY_TILE) };
    case "collision":
      return { ...layer, kind: layer.kind, cells: new Uint8Array(fine) };
    case "zone":
      return { ...layer, kind: layer.kind, types: [], cells: new Uint8Array(fine) };
    case "door":
      return { ...layer, kind: layer.kind, doors: [], cells: new Uint8Array(fine) };
    case "prop":
      return { ...layer, kind: layer.kind, props: [] };
    case "entity":
      return { ...layer, kind: layer.kind, entities: [] };
  }
}

/**
 * Where something goes in a list by its neighbour: at the start with none, right after the neighbour, at the
 * end when the neighbour is gone.
 *
 * @param items The list.
 * @param neighbour The neighbour's id, or null.
 * @returns The index.
 */
function placeBy(items: readonly { readonly id: string }[], neighbour: string | null): number {
  if (neighbour === null) {
    return 0;
  }
  const at = items.findIndex((item) => item.id === neighbour);
  return at < 0 ? items.length : at + 1;
}

/**
 * Resolves a layer's add: the layer empty, above the named layer, with the group to make when the map lacks it.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns The command, nothing when the layer is there as carried, or the reason: `gone` (the map, or the
 *   group the layer names), `taken`, `limit` (the map's layers or groups, or the world's grid bytes).
 */
export function resolveAddLayer(world: WorldDocument, intent: IntentOf<"addLayer">): Resolution {
  const map = mapOf(world, intent.map);
  if (map === undefined) {
    return "gone";
  }
  const existing = map.layers.find((layer) => layer.id === intent.layer.id);
  if (existing !== undefined) {
    return sameJson(withoutContent(existing), intent.layer) ? [] : "taken";
  }
  const groups = map.groups ?? [];
  const makes = intent.group !== undefined && !groups.some((group) => group.id === intent.group?.id);
  if (map.layers.length >= MAP_LAYERS_MAX || (makes && groups.length >= MAP_GROUPS_MAX) || passesGridLimit(world, gridBytes(intent.layer.kind, map))) {
    return "limit";
  }
  const joins = intent.layer.group;
  if (joins !== undefined && !(makes && intent.group?.id === joins) && !groups.some((group) => group.id === joins)) {
    return "gone";
  }
  return [{ kind: "addLayer", mapId: map.id, index: placeBy(map.layers, intent.above), layer: emptyLayer(intent.layer, map), ...(makes && intent.group !== undefined ? { group: intent.group } : {}) }];
}

/**
 * Resolves a layer's removal, with the named group when it is the layer's own and the removal leaves it empty.
 * A group the layer is not in is never removed with it, empty or not.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns The command carrying the layer as held, or nothing when it is already gone.
 */
export function resolveRemoveLayer(world: WorldDocument, intent: IntentOf<"removeLayer">): Resolution {
  const map = mapOf(world, intent.map);
  const index = map?.layers.findIndex((layer) => layer.id === intent.layer) ?? -1;
  if (map === undefined || index < 0) {
    return [];
  }
  const layer = map.layers[index] as LayerDocument;
  const group = (map.groups ?? []).find((candidate) => candidate.id === intent.group && candidate.id === layer.group);
  const emptied = group !== undefined && !map.layers.some((other, i) => i !== index && other.group === group.id);
  return [{ kind: "removeLayer", mapId: map.id, index, layer, ...(emptied ? { group } : {}) }];
}

/**
 * The Players band after a layer moves to a place, by `layer-move.ts`'s rule: the other layers keep their side,
 * and the moved layer lands on the side its place implies. The named side counts only when the place is the
 * band's edge, the one place where the layer could be on either side; anywhere else it is ignored, so a side
 * can never carry other layers across the band.
 *
 * @param band The band before.
 * @param from The layer's index.
 * @param to Its index once moved.
 * @param side The side named, if any.
 * @returns The band after, or null when the move changes nothing.
 */
export function bandAfterPlacing(band: number, from: number, to: number, side: "ground" | "band" | undefined): number | null {
  const edge = from < band ? band - 1 : band;
  return bandAfterMove(band, from, to, side === undefined || to !== edge ? undefined : side === "ground");
}

/**
 * Resolves a layer's move above a named layer, the Players band following by `bandAfterPlacing`. With its
 * neighbour gone the layer goes to the top of the side it names: the band's edge for `ground`, the top of the
 * map for `band` or for no side.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns The command, nothing when the layer is already there on that side, or `gone` when the map or the
 *   layer is.
 */
export function resolveMoveLayer(world: WorldDocument, intent: IntentOf<"moveLayer">): Resolution {
  const map = mapOf(world, intent.map);
  const from = map?.layers.findIndex((layer) => layer.id === intent.layer) ?? -1;
  if (map === undefined || from < 0) {
    return "gone";
  }
  const rest = map.layers.filter((_, index) => index !== from);
  const before = playersAbove(map);
  const lost = intent.above !== null && !rest.some((layer) => layer.id === intent.above);
  const to = lost && intent.side === "ground" ? (from < before ? before - 1 : before) : placeBy(rest, intent.above);
  const after = bandAfterPlacing(before, from, to, intent.side);
  return after === null ? [] : [{ kind: "moveLayer", mapId: map.id, from, to, layerId: intent.layer, playersAbove: { before, after } }];
}

/**
 * Resolves the Players band named by its first layer above.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns The command, nothing when the band is already there, or `gone` when the map or the named layer is.
 */
export function resolveSetPlayersAbove(world: WorldDocument, intent: IntentOf<"setPlayersAbove">): Resolution {
  const map = mapOf(world, intent.map);
  if (map === undefined) {
    return "gone";
  }
  const after = intent.first === null ? map.layers.length : map.layers.findIndex((layer) => layer.id === intent.first);
  if (after < 0) {
    return "gone";
  }
  const before = playersAbove(map);
  return before === after ? [] : [{ kind: "setPlayersAbove", mapId: map.id, before, after }];
}

/**
 * Resolves a group's add.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns The command, nothing when the group is there as carried, or the reason: `gone` (the map), `taken`,
 *   `limit`.
 */
export function resolveAddGroup(world: WorldDocument, intent: IntentOf<"addGroup">): Resolution {
  const map = mapOf(world, intent.map);
  if (map === undefined) {
    return "gone";
  }
  const groups = map.groups ?? [];
  const existing = groups.find((group) => group.id === intent.group.id);
  if (existing !== undefined) {
    return existing.name === intent.group.name ? [] : "taken";
  }
  return groups.length >= MAP_GROUPS_MAX ? "limit" : [{ kind: "addGroup", mapId: map.id, group: intent.group }];
}

/**
 * Resolves a group's removal.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns The command carrying the group as held, nothing when it is already gone, or `held` while a layer
 *   is still in it.
 */
export function resolveRemoveGroup(world: WorldDocument, intent: IntentOf<"removeGroup">): Resolution {
  const map = mapOf(world, intent.map);
  const group = (map?.groups ?? []).find((candidate) => candidate.id === intent.group);
  if (map === undefined || group === undefined) {
    return [];
  }
  return map.layers.some((layer) => layer.group === group.id) ? "held" : [{ kind: "removeGroup", mapId: map.id, group }];
}

/**
 * Resolves a map's add: the map with every layer empty, after the named map.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns The command, nothing when the map is there as carried, or the reason: `taken`, or `limit` for a
 *   full world, a side past `MAP_SIDE_MOST`, more layers or groups than a map takes, properties past theirs, or
 *   grids that would take the world past `WORLD_GRID_BYTES_MAX`.
 */
export function resolveAddMap(world: WorldDocument, intent: IntentOf<"addMap">): Resolution {
  const carried = intent.map;
  const existing = mapOf(world, carried.id);
  if (existing !== undefined) {
    const { place, ...rest } = carried;
    const saved = canonicalPlace(place);
    return sameJson(withoutLayerContent(existing), saved === undefined ? rest : { ...rest, place: saved }) ? [] : "taken";
  }
  if (world.maps.length >= WORLD_MAPS_MAX || carried.width > MAP_SIDE_MOST || carried.height > MAP_SIDE_MOST || carried.layers.length > MAP_LAYERS_MAX || carried.groups.length > MAP_GROUPS_MAX || passesFieldLimits(undefined, carried.properties) || passesGridLimit(world, carried.layers.reduce((bytes, layer) => bytes + gridBytes(layer.kind, carried), 0))) {
    return "limit";
  }
  const band = carried.first === null ? carried.layers.length : carried.layers.findIndex((layer) => layer.id === carried.first);
  const map: MapDocument = {
    id: carried.id,
    name: carried.name,
    width: carried.width,
    height: carried.height,
    seed: carried.seed,
    ...(carried.place === undefined ? {} : { place: canonicalPlace(carried.place) as MapPlace }),
    ...(carried.host === undefined ? {} : { host: carried.host }),
    ...(carried.share === undefined ? {} : { share: carried.share }),
    ...(carried.capacity === undefined ? {} : { capacity: carried.capacity }),
    ...(carried.atmosphere === undefined ? {} : { atmosphere: carried.atmosphere }),
    ...(carried.mapGroup === undefined ? {} : { mapGroup: carried.mapGroup }),
    ...(carried.draft === true ? { draft: true as const } : {}),
    playersAbove: band < 0 ? carried.layers.length : band,
    ...(carried.groups.length === 0 ? {} : { groups: carried.groups }),
    layers: carried.layers.map((layer) => emptyLayer(layer, carried)),
    properties: carried.properties,
  };
  return [{ kind: "addMap", index: placeBy(world.maps, intent.after), map }];
}

/**
 * Resolves a map's removal.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns The command carrying the map as held, nothing when it is already gone, or `last` for the world's
 *   only map.
 */
export function resolveRemoveMap(world: WorldDocument, intent: IntentOf<"removeMap">): Resolution {
  const index = world.maps.findIndex((map) => map.id === intent.map);
  if (index < 0) {
    return [];
  }
  return world.maps.length === 1 ? "last" : [...seamRemovalsFor(world, intent.map), { kind: "removeMap", index, map: world.maps[index] as MapDocument }];
}

/**
 * Resolves a change of the tileset list, each tileset by itself against the document this replica holds
 * (ADR-086 note 20, ADR-129 decision 3.8). One to remove is taken out when nothing names it (a tile cell, a
 * prop's frame, a behaviour's frame or a terrain layer), whole or partial. Of a drop, the tiles named that a
 * partial entry holds and nothing places are taken out; when that would leave the entry no frame the entry is
 * removed if nothing names its tileset, and otherwise nothing of the drop is taken. One to add is appended
 * whole when the list lacks it, and a partial entry is made whole in place. A pull appends a partial entry or
 * widens the one held to the union, ascending, and leaves a whole one. Removals are taken first, then the
 * appended entries stand at the end, `add`'s in its order and then `pull`'s, so appending moves no tileset to
 * another index.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns The one command for what can change; nothing when the list already is as asked; `held` when it
 *   changes nothing and a removal or a drop was stopped by something placed; or `limit`, changing nothing,
 *   when the list would outgrow the packed tile form or a pull would leave its entry past
 *   `PICK_FRAMES_PER_TILESET` or the world's picked frames past `PICK_FRAMES_PER_WORLD`.
 */
export function resolveChangeTilesets(world: WorldDocument, intent: IntentOf<"changeTilesets">): Resolution {
  const named = firstUses(world);
  const held = entriesById(world.tilesets);
  const pull = intent.pull ?? [];
  const drop = intent.drop ?? [];
  const placed = placementsFor(world, drop.map((entry) => entry.id));
  const steps: PickStepOf[] = [
    ...intent.remove.map((id): PickStepOf => [id, removeStep(held(id), named)]),
    ...drop.map((entry): PickStepOf => [entry.id, dropStep(held(entry.id), entry.tiles, () => placed(entry.id), named)]),
    ...intent.add.map((id): PickStepOf => [id, wholeStep(held(id))]),
    ...pull.map((entry): PickStepOf => [entry.id, pullStep(held(entry.id), entry.tiles)]),
  ];
  const command = stepsCommand(world, steps);
  if (command === null) {
    return steps.some(([, step]) => step.kind === "held") ? "held" : [];
  }
  return pickOverflow(world.tilesets, steps, new Set(pull.map((entry) => entry.id))) === null ? [command] : "limit";
}
